"use client";
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import ReactPlayer from "react-player";

import { GroupHeader } from "../../../ui/Headers";
import Button from "../../../ui/Button";
import { EmblaCarouselType } from "embla-carousel/components/EmblaCarousel";
import Pagination from "./Pagination";
import Image from "next/image";
import { useCarouselAnnouncements } from "src/app/lib/useCarouselAnnouncement";
import {
  AnnouncementCardProps,
  isBitChute,
  isYouTube,
  placeholderBlur,
} from "../../design1/components/AnnouncementCard";
import { generateSlugWithId } from "src/app/serverUtils";
// Ensure you have relevant styles
export type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};
export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};
// Define the type for announcement data
export interface Announcement {
  title: string;
  description: string;
  createdAt: string;
  comments: number;
  viewed: number;
  image: string; // URL or path to the image
  // Optional button text
}
// Sample announcement data (replace with your actual data)

const AnnouncementSection3: React.FC = () => {
  const {
    emblaRef,
    scrollSnaps,
    emblaApi,
    selectedIndex,
    onDotButtonClick,
    announcements,
  } = useCarouselAnnouncements();
  return (
    <section className="py-5 pb-14 ">
      <div className="mx-auto">
        <div className="wrapper">
          <GroupHeader
            max={2000}
            subheader="Stay informed with the latest updates, news, and insights to keep you up to date on industry trends and our offerings."
            header="Current Trends and Updates"
          />
        </div>
        <div className="relative wrapper">
          <div className="w-full">
            <div className="embla overflow-hidden" ref={emblaRef}>
              <div className="embla__container items-stretch flex gap-5">
                {announcements.map((announcement, index) => (
                  <div
                    className="embla__slide w-auto select-none shrink-0"
                    key={index}
                  >
                    <AnnouncementCard {...announcement} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pag mt-3 flex justify-center">
            <Pagination
              emblaApi={emblaApi}
              onDotButtonClick={onDotButtonClick}
              selectedIndex={selectedIndex}
              scrollSnaps={scrollSnaps}
              variant
            />
          </div>
        </div>
      </div>
    </section>
  );
};
export default AnnouncementSection3;

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  title,
  image,
  videoLink,
  createdAt,
  id,
  className,
}) => {
  const hasImage = Boolean(image) && image !== "/images/not-found-2.webp";
  const hasVideo = !hasImage && Boolean(videoLink);
  const slugwithid = generateSlugWithId(Number(id), title);

  const formattedDate = createdAt
    ? dayjs(createdAt).format("MMMM D, YYYY")
    : "";
  const href = `/announcements/${slugwithid}`;
  return (
    <Link
      href={href}
      className={`w-[400px] sm:w-[350px] max-w-[400px] xs:max-w-full h-full text-secondary-main overflow-hidden flex flex-col gap-2 rounded-lg ${className}`}
    >
      <div className="w-full h-[14rem] relative rounded-t-sm overflow-hidden">
        {hasImage ? (
          <Image
            height={300}
            width={300}
            loading="lazy"
            src={image!}
            alt={title}
            className="w-full h-full object-cover"
            placeholder="empty"
          />
        ) : hasVideo && isYouTube(videoLink) ? (
          <ReactPlayer
            url={videoLink}
            width="100%"
            height="100%"
            controls
            style={{ objectFit: "cover" }}
          />
        ) : hasVideo && isBitChute(videoLink) ? (
          <iframe
            width="100%"
            height="100%"
            src={videoLink?.replace("/video/", "/embed/")}
            title={title}
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        ) : (
          <Image
            height={300}
            width={300}
            loading="lazy"
            src="/images/not-found-2.webp"
            alt="No preview available"
            className="w-full h-full object-cover"
            placeholder="blur"
            blurDataURL={placeholderBlur}
          />
        )}
      </div>

      <div className="py-4 flex-1 border rounded-b-xl border-themeColor flex flex-col">
        <div className="date py-3 flex justify-end px-3 ml-auto border-b">
          {formattedDate}
        </div>

        <div className="second px-3 flex flex-col flex-grow">
          <h4 className="h6 !font-bold my-2 line-clamp-2 text-ellipsis">
            {title}
          </h4>

          <div className="mt-auto">
            <Button
              className="flex w-max primary-btn !px-2 !pl-3 ml-auto items-center gap-1"
              href={href}
            >
              Read More <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};
